import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { numberFormatter } from '../helpers/formatters';
import { ReusableModal } from '../modals/ReusableModal';
import {
  PriceDefinitionOption,
  PriceDefinitionSpec,
} from '../types/api/PathReport';

type Props = {
  isOpen: boolean;
  endpoint: 'buy' | 'sell';
  options: PriceDefinitionOption[];
  currentDefinition?: PriceDefinitionSpec | null;
  isLoading: boolean;
  isApplying: boolean;
  hasError: boolean;
  onApply: (definition: PriceDefinitionSpec) => void;
  onClose: () => void;
};

const optionKey = (hubId: number, product: string) => `${hubId}:${product}`;

export const PriceDefinitionPickerModal: React.FC<Props> = ({
  isOpen,
  endpoint,
  options,
  currentDefinition,
  isLoading,
  isApplying,
  hasError,
  onApply,
  onClose,
}) => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(
    null,
  );
  const [adder, setAdder] = useState('0');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOptionKey(
      currentDefinition
        ? optionKey(currentDefinition.hub_id, currentDefinition.product)
        : null,
    );
    setAdder(String(currentDefinition?.adder ?? 0));
    setSearch('');
  }, [currentDefinition, isOpen]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      `${option.hub_name} ${option.product} ${option.availability}`
        .toLowerCase()
        .includes(query),
    );
  }, [options, search]);

  const selectedOption = options.find(
    (option) =>
      optionKey(option.hub_id, option.product) === selectedOptionKey,
  );
  const canApplySelectedOption = selectedOption?.availability === 'available';
  const parsedAdder = Number(adder);
  const isAdderValid = adder.trim() !== '' && Number.isFinite(parsedAdder);
  const calculatedPrice =
    canApplySelectedOption && selectedOption.price !== null && isAdderValid
      ? selectedOption.price + parsedAdder
      : null;

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-outline-secondary"
        disabled={isApplying}
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={!canApplySelectedOption || !isAdderValid || isApplying}
        onClick={() => {
          if (!canApplySelectedOption || !selectedOption || !isAdderValid) {
            return;
          }
          onApply({
            hub_id: selectedOption.hub_id,
            product: selectedOption.product,
            adder: parsedAdder,
          });
        }}
      >
        {isApplying ? 'Applying...' : 'Apply'}
      </button>
    </>
  );

  return (
    <ReusableModal
      isOpen={isOpen}
      onClose={onClose}
      header={`Set ${endpoint} price`}
      footer={footer}
      size="lg"
    >
      {isLoading && <LoadingIndicator />}
      {hasError && (
        <div className="alert alert-danger mb-0">
          Price options could not be loaded or applied.
        </div>
      )}
      {!isLoading && !hasError && options.length === 0 && (
        <div className="text-muted">No hubs are configured.</div>
      )}
      {!isLoading && !hasError && options.length > 0 && (
        <>
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search hub or product"
            aria-label="Search price options"
            className="mb-3"
          />
          <div className="table-responsive" style={{ maxHeight: '50vh' }}>
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="sticky-top bg-body">
                <tr>
                  <th aria-label="Selection" />
                  <th>Hub</th>
                  <th>Product</th>
                  <th className="text-end">Current Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOptions.map((option) => {
                  const isAvailable = option.availability === 'available';
                  const key = optionKey(option.hub_id, option.product);
                  return (
                    <tr
                      key={key}
                      role={isAvailable ? 'button' : undefined}
                      className={isAvailable ? undefined : 'text-muted'}
                      onClick={() => {
                        if (isAvailable) setSelectedOptionKey(key);
                      }}
                    >
                      <td>
                        <Input
                          type="radio"
                          name={`${endpoint}-price-definition`}
                          checked={selectedOptionKey === key}
                          disabled={!isAvailable}
                          onChange={() => setSelectedOptionKey(key)}
                          aria-label={`Select ${option.hub_name} ${option.product}`}
                        />
                      </td>
                      <td>{option.hub_name}</td>
                      <td>{option.product}</td>
                      <td className="text-end">
                        {option.price === null
                          ? '-'
                          : `$${numberFormatter(option.price, 3)}`}
                      </td>
                      <td>
                        {option.availability === 'available' ? (
                          <span className="badge text-bg-success">
                            Available
                          </span>
                        ) : option.availability === 'no_quote' ? (
                          <span
                            className="badge text-bg-warning"
                            title="Connector is configured, but no price was returned for this date"
                          >
                            No print
                          </span>
                        ) : (
                          <span
                            className="badge text-bg-secondary"
                            title="No connector is configured for the active price provider"
                          >
                            Not connected
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedOption && (
            <div className="d-flex align-items-end justify-content-end gap-3 border-top mt-3 pt-3">
              <div style={{ width: 160 }}>
                <label className="form-label" htmlFor="price-definition-adder">
                  Adder ($/Dth)
                </label>
                <Input
                  id="price-definition-adder"
                  type="number"
                  step="0.001"
                  value={adder}
                  invalid={!isAdderValid}
                  onChange={(event) => setAdder(event.target.value)}
                />
              </div>
              <div className="text-end" style={{ minWidth: 130 }}>
                <div className="small text-muted">Resulting price</div>
                <div className="fs-5 fw-semibold">
                  {calculatedPrice === null
                    ? '-'
                    : `$${numberFormatter(calculatedPrice, 3)}`}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </ReusableModal>
  );
};